import { Room, nullRoom, nullPlayerSet, nullPlayer, CardSet } from '../Types'
import { Deck, PlayingCard, CardName } from '../Cards'

/**
 * Transform plain object data from backend/SSE into a proper Room object
 * Reconstructs Deck class instances from plain objects
 * This is the equivalent of objToRoom() from the old utils.tsx
 */
export function initRoomFromData(temp: any): Room {
    if (temp === null) {
        // turning data into room yielded null
        return nullRoom
    }

    if (!temp.hasOwnProperty("players")) {
        temp.players = nullPlayerSet
    } else {
        let arr = []
        for (var playerIndex in temp.players) {
            const player = temp.players[playerIndex]
            // Preserve existing hand data, just ensure it has the proper structure
            if (!player.hand) {
                player.hand = { player: player.playerId, deck: new Deck() }
            } else {
                // Convert the deck data to a Deck instance
                if (player.hand.deck && player.hand.deck.cards) {
                    player.hand.deck = new Deck(Object.values(player.hand.deck.cards))
                } else {
                    player.hand.deck = new Deck()
                }
                player.hand.player = player.playerId
            }
            arr.push(player)
        }
        temp.players = arr
    }

    if (!temp.hasOwnProperty("order")) {
        temp.order = []
    } else {
        let arr = []
        for (var playerIndex in temp.order) {
            arr.push(temp.order[playerIndex])
        }
        temp.order = arr
    }

    if (!temp.hasOwnProperty("cardsInPlay")) {
        temp.cardsInPlay = new Deck()
    } else {
        temp.cardsInPlay = new Deck(Object.values(temp.cardsInPlay.cards))
    }

    if (!temp.hasOwnProperty("cardSetFromRound")) {
        temp.cardSetFromRound = []
    } else {
        temp.cardSetFromRound = Object.values(temp.cardSetFromRound)
    }

    if (!temp.hasOwnProperty("cardSetFromLastRound")) {
        temp.cardSetFromLastRound = []
    } else {
        temp.cardSetFromLastRound = Object.values(temp.cardSetFromLastRound)
    }

    // Ensure roundInfo has required fields (for rooms created before these fields were added)
    if (!temp.roundInfo) {
        temp.roundInfo = {
            roundValue: CardName.Joker,
            startPlayer: nullPlayer,
            passNum: -1,
            passedPlayers: []
        }
    } else {
        if (!temp.roundInfo.passedPlayers) {
            temp.roundInfo.passedPlayers = []
        }
    }

    // Ensure roundEndedByPasses flag exists
    if (!temp.hasOwnProperty("roundEndedByPasses")) {
        temp.roundEndedByPasses = false
    }

    let r = temp as Room

    if (r.hasStarted) {
        // Reconstruct Deck instances for card sets from current round
        for (let cardSet of r.cardSetFromRound) {
            if (cardSet.deck === undefined) {
                cardSet.deck = new Deck()
            } else {
                cardSet.deck = new Deck(Object.values(cardSet.deck.cards))
            }
        }

        // Reconstruct Deck instances for card sets from last round
        for (let cardSet of r.cardSetFromLastRound) {
            if (cardSet.deck === undefined) {
                cardSet.deck = new Deck()
            } else {
                cardSet.deck = new Deck(Object.values(cardSet.deck.cards))
            }
        }

        // Reconstruct bluffCaller's hand
        if (r.bluffCaller.hand.deck === undefined) {
            r.bluffCaller.hand.deck = new Deck()
        } else {
            r.bluffCaller.hand.deck = new Deck(Object.values(r.bluffCaller.hand.deck.cards))
        }

        // Reconstruct host's hand
        if (r.host.hand.deck === undefined) {
            r.host.hand.deck = new Deck()
        } else {
            r.host.hand.deck = new Deck(Object.values(r.host.hand.deck.cards))
        }

        // Reconstruct round start player's hand
        if (r.roundInfo.startPlayer.hand.deck === undefined) {
            r.roundInfo.startPlayer.hand.deck = new Deck()
        } else {
            r.roundInfo.startPlayer.hand.deck = new Deck(Object.values(r.roundInfo.startPlayer.hand.deck.cards))
        }

        // Reconstruct all players' hands in order
        // Uses a Map to deduplicate cards for players who appear multiple times
        let dict = new Map()
        for (let i = 0; i < r.order.length; i++) {
            if (r.order[i].hand.deck === undefined) {
                r.order[i].hand.deck = new Deck()
            } else {
                r.order[i].hand.deck = new Deck(r.order[i].hand.deck.cards)
            }

            let cardArr = []
            const tempCards = Object.values(r.order[i].hand.deck.cards)
            for (let j = 0; j < tempCards.length; j++) {
                cardArr.push(new PlayingCard(tempCards[j].cardName, tempCards[j].suit))
            }
            r.order[i].hand.deck = new Deck(cardArr)
            dict.set(r.order[i].playerId, cardArr)
        }

        // Apply the deduplicated cards back to all players
        for (let i = 0; i < r.order.length; i++) {
            r.order[i].hand.deck = new Deck(dict.get(r.order[i].playerId))
        }
    } else {
        // Game hasn't started - all players have empty hands
        r.order.forEach(player => {
            player.hand.deck = new Deck()
        });
    }

    return r
}
